import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { NewButton } from "../../buttons";
import { Icon } from "../../icons/Icon/Icon";
import { Box } from "../../boxes/Box/Box";
import { Label } from "../../labels/Label/Label";
import { Grid } from "../../grids/Grid/Grid";
import { GridItem } from "../../grids/GridItem/GridItem";
import { Like } from "../../icons/Like/Like";
import { ThemeContext, getBrandingLogoUrl } from "../../../utils";

import { useEventListener } from "#hooks";

import "./card-media.scss";

/**
 * CardMedia
 *
 * CardMedia component to be used to display article details
 *
 * @return {jsx}
 */
export const CardMedia = ({
  type,
  size,
  classes,
  onClick,
  image,
  title,
  description,
  showDescription,
  creator,
  labels,
  showLabels,
  readingTime,
  categoryName,
  likes,
  dislikes,
  isLikedByUser,
  isDislikedByUser,
  contentType = "articles",
  isRead,
  isWhiteBackground = false,
  children,
  handlePlay,
  t,
  style,
  countryCode,
  ...props
}) => {
  const { theme } = useContext(ThemeContext);

  const readCountryFromStorage = useCallback(
    () =>
      typeof localStorage !== "undefined"
        ? localStorage.getItem("country") || "KZ"
        : "KZ",
    [],
  );

  const [syncedCountry, setSyncedCountry] = useState(readCountryFromStorage);

  const onCountryChanged = useCallback(() => {
    setSyncedCountry(readCountryFromStorage());
  }, [readCountryFromStorage]);

  useEventListener("countryChanged", onCountryChanged);

  const resolvedCountryForBranding =
    countryCode !== undefined ? countryCode : syncedCountry;

  const brandingFallbackUrl = useMemo(
    () =>
      getBrandingLogoUrl({
        theme,
        countryCode: resolvedCountryForBranding,
      }),
    [theme, resolvedCountryForBranding],
  );

  const displayImageSrc = image || brandingFallbackUrl;
  const isBrandingFallback = !image;

  const whiteBackgroundStyle =
    isWhiteBackground && theme === "light"
      ? { backgroundColor: "#ffffff", ...style }
      : style;

  const renderLabels = () => {
    return labels.map((label, index) => {
      return (
        <Label
          classes={"card-media__label"}
          text={label.name}
          key={index}
          paletteIndex={index}
        />
      );
    });
  };

  const shouldShowPlayIcon =
    handlePlay &&
    (contentType === "videos" ||
      contentType === "podcasts" ||
      contentType === "video" ||
      contentType === "podcast");

  const IS_PS = localStorage.getItem("country") === "PS";
  const IS_PL = localStorage.getItem("country") === "PL";
  const IS_RTL = localStorage.getItem("language") === "ar";

  return (
    <Box
      classes={[
        `card-media card-media--${type} card-media--${size} ${
          isRead ? " card-media--read" : ""
        }`,
        IS_RTL ? " card-media--rtl" : "",
        IS_PL ? " card-media--full-description" : "",
        classNames(classes),
      ].join("")}
      {...props}
      style={whiteBackgroundStyle}
      onClick={onClick}
      liquidGlass
    >
      <div
        className={classNames("card-media__image-container", {
          "card-media__image-container--branding-fallback":
            isBrandingFallback,
        })}
      >
        <img
          className={classNames("card-media__image", {
            "card-media__image--branding-fallback": isBrandingFallback,
          })}
          src={displayImageSrc}
          alt={isBrandingFallback ? "Logo" : title || "card-media"}
          onClick={onClick}
        />
        {shouldShowPlayIcon && (
          <div
            className="card-media__play-overlay"
            onClick={(e) => {
              e.stopPropagation();
              handlePlay();
            }}
          >
            <Icon name="play" color="#ffffff" size="xl" />
          </div>
        )}
      </div>
      {categoryName && (
        <div className="card-media__category">
          <p
            className={`text card-media__category__text ${
              theme === "highContrast" ? "card-media__category__text--hc" : ""
            }`}
          >
            {categoryName}
          </p>
        </div>
      )}
      {isRead && (
        <div className="card-media__read">
          <p className="text card-media__read__text">{t("read")}</p>
        </div>
      )}
      <div className={"card-media__content"}>
        {showLabels && labels?.length > 0 && (
          <div
            className={[
              "card-media__labels",
              type === "landscape" ? "card-media__labels--landscape" : "",
            ].join(" ")}
          >
            {renderLabels()}
          </div>
        )}
        <Grid>
          <GridItem xs={4} md={8} lg={12} classes="card-media__title">
            <h4 className="card-media__title__text">{title}</h4>
          </GridItem>
        </Grid>
        <div className="card-media__content__details">
          <div className="card-media__content__details__left">
            <div className="card-media__content__details__left__read-time-creator-like">
              <div className="card-media__content__details__left__read-time-creator">
                {creator && (
                  <p
                    className={
                      "text card-media__content__details__left__creator"
                    }
                    title={t("by", { creator })}
                  >
                    {t("by", { creator })}
                  </p>
                )}
                <div className={"card-media__details"}>
                  {readingTime && (
                    <React.Fragment>
                      <Icon
                        name="time"
                        size="sm"
                        color={
                          theme === "highContrast"
                            ? "#ffff00" // highContrast color_text_main
                            : theme === "dark"
                              ? "#ededed" // dark color_text_main
                              : "#0e202f" // light color_text_main
                        }
                      />
                      <p className="text">
                        {readingTime} {t("min_read")}
                      </p>
                    </React.Fragment>
                  )}
                </div>
              </div>
              {/* <Like
                isLiked={isLikedByUser}
                isDisliked={isDislikedByUser}
                likes={likes}
                dislikes={dislikes}
              /> */}
            </div>
          </div>
        </div>
        <div className={"card-media__description"}>
          <p className="text" id="description">
            {showDescription && description}
          </p>
        </div>

        <div className="card-media__bottom-container">
          <NewButton
            label={t(
              contentType === "articles" ? "read_more_button" : "view_more",
            )}
            onClick={() => {
              onClick && onClick();
            }}
          />
          {!IS_PS && (
            <Like
              isLiked={isLikedByUser}
              isDisliked={isDislikedByUser}
              likes={likes}
              dislikes={dislikes}
            />
          )}
        </div>
      </div>

      {children}
    </Box>
  );
};

CardMedia.propTypes = {
  /**
   * CardMedia type, the default is primary default is portrait
   **/
  type: PropTypes.oneOf(["portrait", "landscape"]),

  /**
   * Size of the card, the default is sm
   * */
  size: PropTypes.oneOf(["sm", "md", "lg"]),

  /**
   * OnClick function to be called when the "Read more" button is clicked
   * */
  onClick: PropTypes.func,

  /**
   * Image url which will be used to be display an image on the CardMedia
   **/
  image: PropTypes.string,

  /**
   * Title to be displayed
   * */
  title: PropTypes.string,

  /**
   * Description to be displayed
   * */
  description: PropTypes.string,

  /**
   *  Boolean to control if the description to be shown
   * */
  showDescription: PropTypes.bool,

  /**
   * Creator details to be displayed
   * */
  creator: PropTypes.string,

  /**
   * labels to be displayed
   * */
  labels: PropTypes.array,

  /**
   *  Boolean to control if labels are shown or not
   * */
  showLabels: PropTypes.bool,

  /**
   * Reading time to be displayed
   * */
  readingTime: PropTypes.string,

  /**
   * Category name to be displayed
   * */
  categoryName: PropTypes.string,

  /**
   * Content type to determine behavior and styling
   * */
  contentType: PropTypes.oneOf([
    "articles",
    "videos",
    "podcasts",
    "video",
    "podcast",
  ]),

  /**
   * Whether the card background should be white in light theme
   * */
  isWhiteBackground: PropTypes.bool,

  /**
   * Function to handle play action for videos and podcasts
   * */
  handlePlay: PropTypes.func,

  /**
   * Country code for fallback branding image (defaults to localStorage country)
   **/
  countryCode: PropTypes.string,

  /**
   * Additional classes to be added to the CardMedia component
   **/
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

CardMedia.defaultProps = {
  type: "portrait",
  size: "sm",
  classes: "",
  onClick: undefined,
  image: null,
  title: null,
  description: null,
  creator: null,
  labels: [],
  showLabels: true,
  categoryName: null,
  readingTime: null,
  showDescription: true,
  contentType: "articles",
  isWhiteBackground: false,
  handlePlay: undefined,
};
